import React, { useState, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { motion } from 'framer-motion';
import {
  TrendingUp, Landmark, Home, CreditCard, PiggyBank, Car,
  ArrowRight, Info, MessageCircle
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';
import { WHATSAPP_URL } from '../config/contact';
import { CalcActions } from '../components/CalcActions';

const NAVY = '#0A1F44';
const GOLD = '#C0975C';
const serif = { fontFamily: 'Outfit, sans-serif' };

const formatR = (n) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(
    isFinite(n) ? n : 0
  );

// Field: labelled numeric input styled for the dark theme (explicit white text)
const Field = ({ label, value, onChange, prefix, suffix, step, testId }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider text-[#9fb0cc] mb-2">{label}</label>
    <div className="flex items-center gap-2 bg-[#061530] border border-[#C0975C]/25 px-4 py-3 rounded-lg focus-within:border-[#C0975C] transition-colors">
      {prefix && <span className="text-[#C0975C] font-semibold">{prefix}</span>}
      <input
        type="number"
        step={step || '1'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent text-white outline-none text-lg font-semibold"
        data-testid={testId}
      />
      {suffix && <span className="text-[#9fb0cc] text-sm">{suffix}</span>}
    </div>
  </div>
);

const Stat = ({ label, value, accent }) => (
  <div className="p-4 bg-white/5 rounded-lg">
    <p className="text-xs uppercase tracking-wider text-[#9fb0cc] mb-1">{label}</p>
    <p className={`text-2xl font-bold ${accent ? 'text-[#C0975C]' : 'text-white'}`} style={serif}>{value}</p>
  </div>
);

const chartStyle = { background: NAVY, border: `1px solid ${GOLD}` };

const TABS = [
  { id: 'compound', label: 'Compound Growth', icon: TrendingUp },
  { id: 'retirement', label: 'Retirement', icon: Landmark },
  { id: 'bond', label: 'Home Loan', icon: Home },
  { id: 'debt', label: 'Debt Payoff', icon: CreditCard },
  { id: 'tfsa', label: 'Tax-Free Savings', icon: PiggyBank },
  { id: 'vehicle', label: 'Vehicle Finance', icon: Car },
];

// --- Calculation helpers ---
const futureValue = (principal, monthly, years, annualRate) => {
  const r = annualRate / 100 / 12;
  const n = Math.round(years * 12);
  const breakdown = [];
  let value = principal;
  let contributed = principal;
  for (let m = 1; m <= n; m++) {
    value = value * (1 + r) + monthly;
    contributed += monthly;
    if (m % 12 === 0 || m === n) {
      breakdown.push({ year: Math.ceil(m / 12), value: Math.round(value), contributions: Math.round(contributed) });
    }
  }
  return { value, contributed, breakdown };
};

const amortisedPayment = (principal, annualRate, months) => {
  const r = annualRate / 100 / 12;
  if (r === 0) return principal / months;
  return (principal * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
};

export default function InvestmentCalculator() {
  const [tab, setTab] = useState('compound');

  return (
    <Layout>
      <div className="p-6 lg:p-8 max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-4 mb-1">
            <div className="w-14 h-14 bg-[#C0975C] flex items-center justify-center rounded-lg">
              <TrendingUp className="w-7 h-7 text-[#0A1F44]" />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={serif}>
                Financial <span className="text-[#C0975C]">Modelling</span>
              </h1>
              <p className="text-[#9fb0cc]">Professional calculators for planning your wealth</p>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`calc-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-3 font-semibold whitespace-nowrap rounded-lg transition-all ${
                tab === t.id ? 'bg-[#C0975C] text-[#0A1F44]' : 'bg-white/5 text-white hover:bg-white/10'
              }`}
            >
              <t.icon className="w-5 h-5" />
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'compound' && <CompoundCalc />}
        {tab === 'retirement' && <RetirementCalc />}
        {tab === 'bond' && <BondCalc />}
        {tab === 'debt' && <DebtCalc />}
        {tab === 'tfsa' && <TfsaCalc />}
        {tab === 'vehicle' && <VehicleCalc />}

        {/* FAIS: estimates only, route personal advice to the adviser hotline */}
        <div className="mt-8 rounded-2xl border border-[#C0975C]/30 bg-gradient-to-r from-[#122C5A] to-[#0A1F44] p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <p className="text-sm text-[#c6d0e0] flex items-start gap-2 max-w-2xl">
            <Info className="w-5 h-5 text-[#C0975C] flex-shrink-0 mt-0.5" />
            These calculators are general education tools and provide estimates only, not financial advice. For advice tailored to your situation, chat to a qualified Precision Wealth adviser.
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="calc-whatsapp-cta"
            className="neo-button-primary inline-flex items-center justify-center gap-2 px-6 py-3.5 whitespace-nowrap"
          >
            <MessageCircle className="w-4 h-4" /> Advisor WhatsApp Hotline
          </a>
        </div>
      </div>
    </Layout>
  );
}

const Panel = ({ title, children }) => (
  <div className="border border-[#1D3D72] bg-[#122C5A] p-6 sm:p-8 rounded-2xl shadow-xl shadow-black/20">
    <h2 className="text-xl font-bold mb-6" style={serif}>{title}</h2>
    {children}
  </div>
);

function GrowthChart({ data }) {
  return (
    <div className="h-52 mt-6">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data}>
          <defs>
            <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={GOLD} stopOpacity={0.4} />
              <stop offset="95%" stopColor={GOLD} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
          <XAxis dataKey="year" stroke="#9fb0cc" tick={{ fill: '#9fb0cc', fontSize: 12 }} tickFormatter={(v) => `Y${v}`} />
          <YAxis stroke="#9fb0cc" tick={{ fill: '#9fb0cc', fontSize: 12 }} tickFormatter={(v) => (v >= 1000 ? `R${Math.round(v / 1000)}k` : `R${v}`)} />
          <Tooltip contentStyle={chartStyle} labelStyle={{ color: '#fff' }} formatter={(v) => [formatR(v), 'Value']} />
          <Area type="monotone" dataKey="value" stroke={GOLD} strokeWidth={2} fill="url(#g)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// 1. Compound Growth
function CompoundCalc() {
  const [principal, setPrincipal] = useState('10000');
  const [monthly, setMonthly] = useState('1500');
  const [years, setYears] = useState('20');
  const [rate, setRate] = useState('10');

  const res = useMemo(
    () => futureValue(+principal || 0, +monthly || 0, +years || 0, +rate || 0),
    [principal, monthly, years, rate]
  );
  const interest = res.value - res.contributed;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-2 gap-6">
      <Panel title="Compound Growth">
        <div className="space-y-5">
          <Field label="Starting Amount" prefix="R" value={principal} onChange={setPrincipal} testId="compound-principal" />
          <Field label="Monthly Contribution" prefix="R" value={monthly} onChange={setMonthly} testId="compound-monthly" />
          <Field label="Investment Period" suffix="years" value={years} onChange={setYears} testId="compound-years" />
          <Field label="Expected Annual Return" suffix="%" step="0.1" value={rate} onChange={setRate} testId="compound-rate" />
        </div>
      </Panel>
      <Panel title={`Projected Value After ${years || 0} Years`}>
        <div className="text-center mb-6 p-6 bg-gradient-to-br from-[#C0975C]/20 to-transparent rounded-lg">
          <p className="text-[#9fb0cc] text-sm mb-1">Future Value</p>
          <p className="text-4xl font-bold text-[#C0975C]" style={serif} data-testid="compound-result">{formatR(res.value)}</p>
        </div>
        <CalcActions title="Compound Growth" rows={[{label:'Starting amount',value:formatR(+principal||0)},{label:'Monthly contribution',value:formatR(+monthly||0)},{label:'Period',value:`${years||0} years`},{label:'Expected return',value:`${rate||0}%`},{label:'You invested',value:formatR(res.contributed)},{label:'Growth earned',value:formatR(interest)}]} result={{label:'Projected future value',value:formatR(res.value)}} />
        <div className="grid grid-cols-2 gap-4">
          <Stat label="You Invested" value={formatR(res.contributed)} />
          <Stat label="Growth Earned" value={formatR(interest)} accent />
        </div>
        <GrowthChart data={res.breakdown} />
      </Panel>
    </motion.div>
  );
}

// 2. Retirement Planner
function RetirementCalc() {
  const [age, setAge] = useState('35');
  const [retireAge, setRetireAge] = useState('65');
  const [savings, setSavings] = useState('150000');
  const [monthly, setMonthly] = useState('3000');
  const [rate, setRate] = useState('9');

  const years = Math.max(0, (+retireAge || 0) - (+age || 0));
  const res = useMemo(
    () => futureValue(+savings || 0, +monthly || 0, years, +rate || 0),
    [savings, monthly, years, rate]
  );
  const monthlyIncome = (res.value * 0.04) / 12; // 4% safe withdrawal rule

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-2 gap-6">
      <Panel title="Retirement Planner">
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Current Age" value={age} onChange={setAge} testId="ret-age" />
            <Field label="Retirement Age" value={retireAge} onChange={setRetireAge} testId="ret-retire-age" />
          </div>
          <Field label="Current Savings" prefix="R" value={savings} onChange={setSavings} testId="ret-savings" />
          <Field label="Monthly Contribution" prefix="R" value={monthly} onChange={setMonthly} testId="ret-monthly" />
          <Field label="Expected Annual Return" suffix="%" step="0.1" value={rate} onChange={setRate} testId="ret-rate" />
        </div>
      </Panel>
      <Panel title={`Projected Pot at Age ${retireAge || 0}`}>
        <div className="text-center mb-6 p-6 bg-gradient-to-br from-[#C0975C]/20 to-transparent rounded-lg">
          <p className="text-[#9fb0cc] text-sm mb-1">Retirement Capital</p>
          <p className="text-4xl font-bold text-[#C0975C]" style={serif} data-testid="ret-result">{formatR(res.value)}</p>
        </div>
        <CalcActions title="Retirement Planner" rows={[{label:'Current age',value:age},{label:'Retirement age',value:retireAge},{label:'Current savings',value:formatR(+savings||0)},{label:'Monthly contribution',value:formatR(+monthly||0)},{label:'Total contributed',value:formatR(res.contributed)},{label:'Est. monthly income',value:formatR(monthlyIncome)}]} result={{label:'Projected retirement capital',value:formatR(res.value)}} />
        <div className="grid grid-cols-2 gap-4 mb-4">
          <Stat label="Total Contributed" value={formatR(res.contributed)} />
          <Stat label="Est. Monthly Income" value={formatR(monthlyIncome)} accent />
        </div>
        <p className="flex items-start gap-2 text-xs text-[#9fb0cc]">
          <Info className="w-4 h-4 text-[#C0975C] shrink-0 mt-0.5" />
          Monthly income estimated using a 4% annual safe-withdrawal rule. Speak to a Precision Wealth adviser for a tailored plan.
        </p>
        <GrowthChart data={res.breakdown} />
      </Panel>
    </motion.div>
  );
}

// 3. Home Loan (Bond) Repayment
function BondCalc() {
  const [amount, setAmount] = useState('1500000');
  const [rate, setRate] = useState('11.75');
  const [term, setTerm] = useState('20');

  const months = (+term || 0) * 12;
  const payment = useMemo(() => amortisedPayment(+amount || 0, +rate || 0, months), [amount, rate, months]);
  const totalPaid = payment * months;
  const totalInterest = totalPaid - (+amount || 0);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-2 gap-6">
      <Panel title="Home Loan (Bond) Repayment">
        <div className="space-y-5">
          <Field label="Property / Loan Amount" prefix="R" value={amount} onChange={setAmount} testId="bond-amount" />
          <Field label="Interest Rate (annual)" suffix="%" step="0.05" value={rate} onChange={setRate} testId="bond-rate" />
          <Field label="Loan Term" suffix="years" value={term} onChange={setTerm} testId="bond-term" />
        </div>
      </Panel>
      <Panel title="Your Repayment">
        <div className="text-center mb-6 p-6 bg-gradient-to-br from-[#C0975C]/20 to-transparent rounded-lg">
          <p className="text-[#9fb0cc] text-sm mb-1">Monthly Repayment</p>
          <p className="text-4xl font-bold text-[#C0975C]" style={serif} data-testid="bond-result">{formatR(payment)}</p>
        </div>
        <CalcActions title="Home Loan (Bond) Repayment" rows={[{label:'Loan amount',value:formatR(+amount||0)},{label:'Interest rate',value:`${rate||0}%`},{label:'Term',value:`${term||0} years`},{label:'Total repaid',value:formatR(totalPaid)},{label:'Total interest',value:formatR(totalInterest)}]} result={{label:'Monthly repayment',value:formatR(payment)}} />
        <div className="grid grid-cols-2 gap-4">
          <Stat label="Total Repaid" value={formatR(totalPaid)} />
          <Stat label="Total Interest" value={formatR(totalInterest)} accent />
        </div>
      </Panel>
    </motion.div>
  );
}

// 4. Debt Payoff
function DebtCalc() {
  const [balance, setBalance] = useState('50000');
  const [rate, setRate] = useState('18');
  const [payment, setPayment] = useState('2000');

  const r = (+rate || 0) / 100 / 12;
  const minInterest = (+balance || 0) * r;
  const feasible = (+payment || 0) > minInterest;
  const months = feasible
    ? Math.ceil(-Math.log(1 - ((+balance || 0) * r) / (+payment || 0)) / Math.log(1 + r))
    : 0;
  const totalPaid = feasible ? (+payment || 0) * months : 0;
  const totalInterest = feasible ? totalPaid - (+balance || 0) : 0;
  const yrs = Math.floor(months / 12);
  const mos = months % 12;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-2 gap-6">
      <Panel title="Debt Payoff">
        <div className="space-y-5">
          <Field label="Outstanding Balance" prefix="R" value={balance} onChange={setBalance} testId="debt-balance" />
          <Field label="Interest Rate (annual)" suffix="%" step="0.1" value={rate} onChange={setRate} testId="debt-rate" />
          <Field label="Monthly Payment" prefix="R" value={payment} onChange={setPayment} testId="debt-payment" />
        </div>
      </Panel>
      <Panel title="Time to Freedom">
        {feasible ? (
          <>
            <div className="text-center mb-6 p-6 bg-gradient-to-br from-[#C0975C]/20 to-transparent rounded-lg">
              <p className="text-[#9fb0cc] text-sm mb-1">Debt Cleared In</p>
              <p className="text-4xl font-bold text-[#C0975C]" style={serif} data-testid="debt-result">
                {yrs > 0 ? `${yrs}y ` : ''}{mos}m
              </p>
            </div>
            <CalcActions title="Debt Payoff" rows={[{label:'Outstanding balance',value:formatR(+balance||0)},{label:'Interest rate',value:`${rate||0}%`},{label:'Monthly payment',value:formatR(+payment||0)},{label:'Total repaid',value:formatR(totalPaid)},{label:'Interest paid',value:formatR(totalInterest)}]} result={{label:'Debt cleared in',value:`${yrs>0?`${yrs}y `:''}${mos}m`}} />
            <div className="grid grid-cols-2 gap-4">
              <Stat label="Total Repaid" value={formatR(totalPaid)} />
              <Stat label="Interest Paid" value={formatR(totalInterest)} accent />
            </div>
          </>
        ) : (
          <div className="p-6 bg-[#DE3831]/10 border border-[#DE3831]/30 rounded-lg text-center">
            <p className="text-[#ff6b6b] font-semibold mb-1">Payment too low</p>
            <p className="text-sm text-[#9fb0cc]">
              Your monthly payment does not cover the monthly interest of {formatR(minInterest)}. Increase your payment to start reducing the balance.
            </p>
          </div>
        )}
      </Panel>
    </motion.div>
  );
}

// 5. Tax-Free Savings
function TfsaCalc() {
  const [monthly, setMonthly] = useState('3000');
  const [years, setYears] = useState('15');
  const [rate, setRate] = useState('9');

  const annual = (+monthly || 0) * 12;
  const overAnnual = annual > 46000;
  const res = useMemo(() => futureValue(0, +monthly || 0, +years || 0, +rate || 0), [monthly, years, rate]);
  const lifetimeContrib = Math.min(res.contributed, 500000);
  const overLifetime = res.contributed > 500000;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-2 gap-6">
      <Panel title="Tax-Free Savings (TFSA)">
        <div className="space-y-5">
          <Field label="Monthly Contribution" prefix="R" value={monthly} onChange={setMonthly} testId="tfsa-monthly" />
          <Field label="Investment Period" suffix="years" value={years} onChange={setYears} testId="tfsa-years" />
          <Field label="Expected Annual Return" suffix="%" step="0.1" value={rate} onChange={setRate} testId="tfsa-rate" />
          {(overAnnual || overLifetime) && (
            <p className="flex items-start gap-2 text-xs text-[#ff6b6b]">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              {overAnnual ? 'Exceeds the R46,000 annual TFSA limit. ' : ''}
              {overLifetime ? 'Exceeds the R500,000 lifetime TFSA limit. ' : ''}
              Contributions above these limits are taxed at 40%.
            </p>
          )}
        </div>
      </Panel>
      <Panel title={`Tax-Free Value After ${years || 0} Years`}>
        <div className="text-center mb-6 p-6 bg-gradient-to-br from-[#C0975C]/20 to-transparent rounded-lg">
          <p className="text-[#9fb0cc] text-sm mb-1">Projected Value</p>
          <p className="text-4xl font-bold text-[#C0975C]" style={serif} data-testid="tfsa-result">{formatR(res.value)}</p>
        </div>
        <CalcActions title="Tax-Free Savings (TFSA)" rows={[{label:'Monthly contribution',value:formatR(+monthly||0)},{label:'Period',value:`${years||0} years`},{label:'Expected return',value:`${rate||0}%`},{label:'Contributed',value:formatR(lifetimeContrib)},{label:'Growth (tax-free)',value:formatR(res.value-res.contributed)}]} result={{label:'Projected tax-free value',value:formatR(res.value)}} />
        <div className="grid grid-cols-2 gap-4">
          <Stat label="Contributed" value={formatR(lifetimeContrib)} />
          <Stat label="Growth (Tax-Free)" value={formatR(res.value - res.contributed)} accent />
        </div>
        <GrowthChart data={res.breakdown} />
      </Panel>
    </motion.div>
  );
}

// 6. Vehicle Finance
function VehicleCalc() {
  const [price, setPrice] = useState('350000');
  const [deposit, setDeposit] = useState('35000');
  const [balloon, setBalloon] = useState('10');
  const [rate, setRate] = useState('12.5');
  const [term, setTerm] = useState('6');

  const balloonAmt = ((+price || 0) * (+balloon || 0)) / 100;
  const financed = Math.max(0, (+price || 0) - (+deposit || 0) - balloonAmt);
  const months = (+term || 0) * 12;
  const r = (+rate || 0) / 100 / 12;
  // Standard installment sale: finance (price - deposit), balloon owed as residual at the end
  const monthlyPay = useMemo(() => {
    if (months === 0) return 0;
    if (r === 0) return financed / months;
    return (r * (financed - balloonAmt / Math.pow(1 + r, months))) / (1 - Math.pow(1 + r, -months));
  }, [financed, balloonAmt, r, months]);
  const totalPaid = monthlyPay * months + balloonAmt + (+deposit || 0);
  const totalInterest = totalPaid - (+price || 0);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-2 gap-6">
      <Panel title="Vehicle Finance">
        <div className="space-y-5">
          <Field label="Vehicle Price" prefix="R" value={price} onChange={setPrice} testId="veh-price" />
          <Field label="Deposit" prefix="R" value={deposit} onChange={setDeposit} testId="veh-deposit" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Balloon Payment" suffix="%" value={balloon} onChange={setBalloon} testId="veh-balloon" />
            <Field label="Term" suffix="years" value={term} onChange={setTerm} testId="veh-term" />
          </div>
          <Field label="Interest Rate (annual)" suffix="%" step="0.05" value={rate} onChange={setRate} testId="veh-rate" />
        </div>
      </Panel>
      <Panel title="Your Installment">
        <div className="text-center mb-6 p-6 bg-gradient-to-br from-[#C0975C]/20 to-transparent rounded-lg">
          <p className="text-[#9fb0cc] text-sm mb-1">Monthly Installment</p>
          <p className="text-4xl font-bold text-[#C0975C]" style={serif} data-testid="veh-result">{formatR(monthlyPay)}</p>
        </div>
        <CalcActions title="Vehicle Finance" rows={[{label:'Vehicle price',value:formatR(+price||0)},{label:'Deposit',value:formatR(+deposit||0)},{label:'Balloon payment',value:`${balloon||0}%`},{label:'Term',value:`${term||0} years`},{label:'Balloon due at end',value:formatR(balloonAmt)},{label:'Total interest',value:formatR(totalInterest)}]} result={{label:'Monthly installment',value:formatR(monthlyPay)}} />
        <div className="grid grid-cols-2 gap-4">
          <Stat label="Balloon Due at End" value={formatR(balloonAmt)} />
          <Stat label="Total Interest" value={formatR(totalInterest)} accent />
        </div>
      </Panel>
    </motion.div>
  );
}
